'use client';

import { Clock } from 'lucide-react';

import type {
  MeetingSlot,
  MeetingTypeItem,
} from '../hooks/use-available-dates';
import { formatSlotTime } from '../utils/format';

// ─── Component ────────────────────────────────────────────────────────────

export function MeetingTypeCard({
  meetingType,
  isSelected,
  isLoadingSlots,
  selectedSlot,
  showChange,
  compact = false,
  onSelect,
  onSlotSelect,
  onChangeType,
}: {
  meetingType: MeetingTypeItem;
  isSelected: boolean;
  isLoadingSlots: boolean;
  selectedSlot: MeetingSlot | null;
  showChange: boolean;
  compact?: boolean;
  onSelect?: (mt: MeetingTypeItem) => void;
  onSlotSelect: (slot: MeetingSlot) => void;
  onChangeType?: () => void;
}) {
  const textSize = compact ? 'text-[10px]' : 'text-sm';
  const subTextSize = compact ? 'text-[8px]' : 'text-xs';

  return (
    <div
      className={`rounded-lg border transition-colors ${compact ? 'p-1.5' : 'p-3'} ${
        isSelected
          ? 'force-light:border-gray-900 force-light:bg-gray-50 border-gray-900 bg-gray-50 dark:border-white dark:bg-gray-800'
          : 'force-light:border-gray-200 force-light:hover:border-gray-300 border-gray-200 hover:border-gray-300 dark:border-gray-700 dark:hover:border-gray-600'
      }`}
    >
      {isSelected ? (
        <div className="flex w-full items-center justify-between">
          <span
            className={`${textSize} force-light:text-gray-900 font-medium text-gray-900 dark:text-white`}
          >
            {meetingType.topic}
          </span>
          <div className="flex items-center gap-2">
            <span
              className={`flex items-center gap-1 ${subTextSize} force-light:text-gray-500 text-gray-500 dark:text-gray-400`}
            >
              <Clock className={compact ? 'h-2.5 w-2.5' : 'h-3 w-3'} />
              {meetingType.durationMinutes} min
            </span>
            {showChange && (
              <button
                type="button"
                onClick={() => onChangeType?.()}
                className={`${subTextSize} force-light:text-gray-500 force-light:hover:text-gray-700 font-medium text-gray-500 transition-colors hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200`}
              >
                Change
              </button>
            )}
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => onSelect?.(meetingType)}
          className="flex w-full items-center justify-between text-left"
        >
          <span
            className={`${textSize} force-light:text-gray-900 font-medium text-gray-900 dark:text-white`}
          >
            {meetingType.topic}
          </span>
          <div className="flex items-center gap-2">
            <span
              className={`flex items-center gap-1 ${subTextSize} force-light:text-gray-500 text-gray-500 dark:text-gray-400`}
            >
              <Clock className={compact ? 'h-2.5 w-2.5' : 'h-3 w-3'} />
              {meetingType.durationMinutes} min
            </span>
          </div>
        </button>
      )}

      {/* Slots section (only when selected) */}
      {isSelected &&
        (isLoadingSlots ? (
          <div
            className={`${compact ? 'mt-2' : 'mt-3'} flex items-center justify-center gap-2 py-2`}
          >
            <p
              className={`${subTextSize} force-light:text-gray-400 text-gray-400 dark:text-gray-500`}
            >
              Checking for available time slots
            </p>
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600" />
          </div>
        ) : meetingType.slots.length > 0 ? (
          <div className={`${compact ? 'mt-2' : 'mt-3'} flex flex-wrap gap-2`}>
            {meetingType.slots.map(slot => {
              const isSlotSelected = selectedSlot?.startTime === slot.startTime;
              return (
                <button
                  key={slot.startTime}
                  type="button"
                  onClick={() => onSlotSelect(slot)}
                  className={`font-medium transition-colors ${
                    compact
                      ? 'rounded border px-1.5 py-0.5 text-[8px]'
                      : 'rounded-md px-3 py-1.5 text-xs'
                  } ${
                    isSlotSelected
                      ? 'force-light:bg-gray-900 force-light:text-white bg-gray-900 text-white dark:bg-white dark:text-gray-900' +
                        (compact
                          ? ' force-light:border-gray-900 border-gray-900'
                          : '')
                      : 'force-light:bg-gray-200 force-light:text-gray-700 force-light:hover:bg-gray-300 bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600' +
                        (compact
                          ? ' force-light:border-gray-300 border-gray-300'
                          : '')
                  }`}
                >
                  {formatSlotTime(slot.startTime)}
                </button>
              );
            })}
          </div>
        ) : (
          <p
            className={`${compact ? 'mt-2' : 'mt-3'} py-2 text-center ${subTextSize} force-light:text-gray-400 text-gray-400 dark:text-gray-500`}
          >
            No available time slots.
          </p>
        ))}
    </div>
  );
}
